import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router'
import { Controller } from 'react-hook-form'
import { KeyRound, Lock, Mail } from 'lucide-react'

import { AppleLogo, GoogleLogo, LockupWhite } from '@/assets'
import EyeOnIcon from '@/assets/eye-password-on.svg?react'
import EyeOffIcon from '@/assets/eye-password-off.svg?react'
import Text from '@/elements/Text'
import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import FormInput from '@/elements/FormInput'
import Loading from '@/elements/Loading'
import useAuthStore, { startAuth } from '@/stores/authStore'
import { saveInviteFlowParams } from '@/utils/inviteFlow'
import { useTheme } from '@/utils/theme'
import useBreakpoints from '@/hooks/useBreakpoints'
import useSetPasswordForm from '@/hooks/forms/useSetPasswordForm'
import { authSetup, respondToChallenge, validateInviteToken, type AuthChallengeResponse } from '@/api/auth'
import toast from '@/utils/toast'

function AuthSetup() {
  const navigate = useNavigate()
  const { isMobile } = useBreakpoints()
  const { theme } = useTheme()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite_token') ?? ''
  const emailParam = searchParams.get('email') ?? ''

  const [validating, setValidating] = useState(true)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)
  const [flashError, setFlashError] = useState(false)
  const [mfaCode, setMfaCode] = useState('')
  const [challengeData, setChallengeData] = useState<AuthChallengeResponse | null>(null)

  const setCredentials = useAuthStore(state => state.setCredentials)
  const initialize = useAuthStore(state => state.initialize)

  const form = useSetPasswordForm()

  useEffect(() => {
    saveInviteFlowParams()

    if (!inviteToken) {
      navigate('/', { replace: true })
      return
    }

    validateInviteToken(inviteToken, emailParam || undefined)
      .then(() => setValidating(false))
      .catch(() => {
        toast.error('Invalid or expired invite link.')
        navigate('/', { replace: true })
      })
  }, [inviteToken, emailParam, navigate])

  const handlePasswordSetup = async (data: { newPassword: string }) => {
    if (!inviteToken) {
      toast.error('Invalid invite link. Please use the link from your email.')
      return
    }
    setLoading(true)
    try {
      const result = await authSetup({
        invite_token: inviteToken,
        password: data.newPassword,
        email: emailParam || undefined,
      })

      if (result.type === 'authenticated') {
        setCredentials({
          access_token: result.tokens.accessToken,
          id_token: result.tokens.idToken,
          refresh_token: result.tokens.refreshToken,
          expires_in: result.tokens.expiresIn,
          token_type: 'Bearer',
        })
        navigate('/', { replace: true })
        await initialize(false)
      } else {
        setChallengeData(result)
        toast.info('Please enter the verification code sent to your email')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Account setup failed'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleMfaSubmit = async () => {
    if (!challengeData) return

    setLoading(true)
    try {
      const result = await respondToChallenge({
        challengeName: challengeData.challengeName,
        session: challengeData.session,
        username: challengeData.username,
        code: mfaCode,
      })

      if (result.type !== 'authenticated') {
        toast.error('Verification failed. Please try again.')
        return
      }

      setCredentials({
        access_token: result.tokens.accessToken,
        id_token: result.tokens.idToken,
        refresh_token: result.tokens.refreshToken,
        expires_in: result.tokens.expiresIn,
        token_type: 'Bearer',
      })
      navigate('/', { replace: true })
      await initialize(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Verification failed. Please try again.'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  if (validating) return (
    <Flex align='center' justify='center' style={{ minHeight: '100svh' }}>
      <Loading />
    </Flex>
  )

  const pwdError = form.formState.errors.newPassword?.message
  const bannerError = (pwdError !== 'Password must be at least 8 characters' ? pwdError : undefined)
    ?? form.formState.errors.passwordConfirm?.message

  return (
    <Flex
      component='main'
      align='center'
      justify='center'
      gap={!isMobile ? 6 : 4}
      py={!isMobile ? 7 : 5}
      px={4}
      className='auth'
      style={{ minHeight: '100svh' }}
    >
      <LockupWhite
        style={{
          width: 162,
          maxWidth: '80%',
          filter: theme === 'dark' ? 'invert(0)' : 'invert(1)',
        }}
      />

      <Flex
        align='center'
        gap={4}
        grow
        style={{ width: !isMobile ? 306 : '100%', maxWidth: '100%' }}
      >
        {challengeData ? (
          <>
            <Flex align='center'>
              <Text variant='h1' style={{ fontSize: '2rem' }}>
                Verify your identity
              </Text>
              <Text variant='textblock' color='black-light-80' align='center'>
                Enter the verification code sent to your email
              </Text>
            </Flex>

            <Flex gap={3} wfull>
              <FormInput
                type='text'
                label='Verification Code'
                placeholder='Enter the code from your email'
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value)}
                style={{ width: '100%' }}
                Icon={KeyRound}
                autoFocus
              />
              <Flex wfull gap={2}>
                <Button
                  variant='accent'
                  onClick={handleMfaSubmit}
                  disabled={loading || !mfaCode}
                  fullWidth
                >
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </Button>
                <Button
                  variant={theme === 'dark' ? 'secondary' : 'primary'}
                  onClick={() => {
                    setChallengeData(null)
                    setMfaCode('')
                  }}
                  disabled={loading}
                  fullWidth
                >
                  Back
                </Button>
              </Flex>
            </Flex>
          </>
        ) : (
          <>
            <Flex align='center'>
              <Text variant='h1' style={{ fontSize: '2rem' }}>
                Welcome!
              </Text>
              <Text variant='textblock' color='black-light-10' align='center'>
                Set up your account to continue
              </Text>
            </Flex>

            <div style={{ position: 'relative', height: 0, width: '100%' }}>
              {flashError && bannerError && (
                <div style={{
                  position: 'absolute',
                  top: -20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'var(--color-red)',
                  borderRadius: 9999,
                  padding: '10px 20px',
                  whiteSpace: 'nowrap',
                  animation: 'bannerFlash 2.5s ease forwards',
                }}>
                  <Text variant='callout' style={{ color: 'var(--color-white)', fontWeight: 600, fontSize: '0.7rem' }}>
                    {bannerError}
                  </Text>
                </div>
              )}
            </div>

            <form
              onSubmit={form.handleSubmit(
                data => handlePasswordSetup(data),
                () => {
                  setFlashError(true)
                  setTimeout(() => setFlashError(false), 2500)
                }
              )}
              style={{ width: '100%' }}
            >
              <Flex gap={3} wfull>
                {emailParam && (
                  <FormInput
                    type='email'
                    label='Email'
                    value={emailParam}
                    disabled
                    style={{ width: '100%' }}
                    Icon={Mail}
                  />
                )}

                <Controller
                  control={form.control}
                  name='newPassword'
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      label='Password'
                      placeholder='Enter your password'
                      hasError={flashError && !!form.formState.errors.newPassword}
                      dirty={!!form.formState.dirtyFields.newPassword}
                      instructions='At least 8 characters'
                      style={{ width: '100%' }}
                      Icon={Lock}
                      rightAction={
                        <button
                          type='button'
                          onClick={() => setShowPassword(v => !v)}
                        >
                          {showPassword ? (
                            <EyeOffIcon width={22} height={22} />
                          ) : (
                            <EyeOnIcon width={22} height={22} />
                          )}
                        </button>
                      }
                    />
                  )}
                />

                <Controller
                  control={form.control}
                  name='passwordConfirm'
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      type={showPasswordConfirm ? 'text' : 'password'}
                      label='Confirm Password'
                      placeholder='Re-enter your password'
                      hasError={flashError && !!form.formState.errors.passwordConfirm}
                      dirty={!!form.formState.dirtyFields.passwordConfirm}
                      style={{ width: '100%' }}
                      Icon={Lock}
                      rightAction={
                        <button
                          type='button'
                          onClick={() => setShowPasswordConfirm(v => !v)}
                        >
                          {showPasswordConfirm ? (
                            <EyeOffIcon width={22} height={22} />
                          ) : (
                            <EyeOnIcon width={22} height={22} />
                          )}
                        </button>
                      }
                    />
                  )}
                />

                <Flex wfull>
                  <Button
                    submit
                    variant='accent'
                    disabled={loading || !inviteToken}
                    fullWidth
                  >
                    {loading ? 'Setting up…' : 'Set Password & Continue'}
                  </Button>
                </Flex>
              </Flex>
            </form>

            <Flex inline align='center' gap={3} wfull>
              <Flex style={{ height: 1 }} color='gray-light-20' wfull />
              <Text
                variant='callout'
                color='black-light-60'
                style={{ whiteSpace: 'nowrap' }}
              >
                or
              </Text>
              <Flex style={{ height: 1 }} color='gray-light-20' wfull />
            </Flex>

            <Flex gap={3} align='center'>
              <Button
                className='button'
                onClick={() => startAuth('apple')}
                styles={{ width: '100%' }}
                variant={'secondary'}
              >
                <AppleLogo width={30} height={30} />
                Continue with Apple
              </Button>
              <Button
                className='button'
                onClick={() => startAuth('google')}
                styles={{ width: '100%' }}
                variant={'secondary'}
              >
                <GoogleLogo width={22} height={22} />
                Continue with Google
              </Button>

              <Flex
                style={{ height: 1, marginTop: 8 }}
                color='gray-light-20'
                wfull
              />
              <Text
                variant='textblock'
                style={{ color: 'var(--color-terms-of-service-main)' }}
                align='center'
              >
                By continuing, you agree to our{' '}
                <span style={{ color: 'var(--color-terms-of-service)' }}>
                  Terms of Service
                </span>{' '}
                and{' '}
                <span style={{ color: 'var(--color-terms-of-service)' }}>
                  Privacy Policy
                </span>
                .
              </Text>
            </Flex>
          </>
        )}
      </Flex>
    </Flex>
  )
}

export default AuthSetup
